/**
 * VilaxHeader — the full Vilax site header (Volto 19.1.1 / VLT-8, converged).
 *
 * Registered in VLT's `aboveHeader` slot (via applyConfig in index.js). VLT's
 * Header is shadowed at `customizations/@kitconcept/volto-light-theme/
 * components/Header/Header.jsx` to render only the aboveHeader + belowHeader
 * slots (no <header className="header-wrapper">), so this component is the only
 * header the visitor or assistive tech sees (BUG-2026-116).
 *
 * VLT-8 port (DR-2026-203 frontend-A, spike-proven): the overlay/solid logic,
 * the aboveHeader mount, and the Header shadow all port verbatim from VLT-7
 * (coupling #1 blockWidth + #2 slot/Header both survive VLT-8). The ONE change
 * vs the VLT-7 original is the mobile nav: the #1 shadow-paydown replaces VLT's
 * `MobileNavigation` (+ the deleted MobileToolsFooter + ~180 lines of
 * pastanaga-hamburger neutralization) with the bespoke, owned `VilaxMobileNav`
 * (`.vmn-*`) — zero VLT-internal coupling, nothing upstream to fight.
 *
 * Overlay/solid logic (unchanged from DR-2026-055 §14):
 *   - Full-bleed image first block  → overlay (transparent, absolute, z-index 15)
 *   - Anything else / non-content   → solid (white background, in-flow)
 */
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import LanguageSelector from '@plone/volto/components/theme/LanguageSelector/LanguageSelector';
import Logo from '@plone/volto/components/theme/Logo/Logo';
import Navigation from '@plone/volto/components/theme/Navigation/Navigation';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';
import VilaxMobileNav from './VilaxMobileNav';

/**
 * URL substrings that always force solid mode — non-content routes where the
 * overlay header would cover edit chrome or render over stale hero data held
 * in Redux.
 */
const SOLID_ROUTES = [
  '/search',
  '/login',
  '/logout',
  '/register',
  '/controlpanel',
  '/personal-information',
  '/change-password',
  '/add',
  '/edit',
  '/manage-',
  '/contents',
  '/sharing',
  '/history',
];

/**
 * Decide whether to render the overlay variant.  Receives the raw browser
 * pathname (not the content-stripped Volto prop) so `/sv/login` is correctly
 * detected as a non-content route even though Redux content still holds the
 * /sv hero.
 */
const shouldUseOverlay = (content, actualPathname) => {
  try {
    if (SOLID_ROUTES.some((route) => actualPathname?.includes(route))) {
      return false;
    }
    const items = content?.blocks_layout?.items;
    if (!items || !Array.isArray(items) || items.length === 0) return false;

    const firstBlock = content?.blocks?.[items[0]];
    if (!firstBlock) return false;

    if (firstBlock['@type'] === 'image') {
      // VLT's BlockWidth widget saves action.style (an object) under this
      // key, NOT the preset name. VLT-8's "Full" preset writes
      // {'--block-width': '100%'} (config/blocks.tsx); VLT-7 wrote
      // {'--block-width': 'unset'}; legacy Vilax content wrote
      // {'--block-width': '100%'}; pre-ButtonsWidget VLT wrote the bare
      // string 'full'. Accept all so old + new content both trigger overlay.
      // Coupling #1 — SURVIVES VLT-8 (no 4th shape appeared). BUG-2026-098.
      const bw = firstBlock.styles?.['blockWidth:noprefix'];
      if (bw === 'full') return true;
      const v = bw && typeof bw === 'object' ? bw['--block-width'] : undefined;
      return v === 'unset' || v === '100%';
    }
    return false;
  } catch (e) {
    return false;
  }
};

const VilaxHeader = () => {
  const location = useLocation();
  const content = useSelector((state) => state.content?.data, shallowEqual);
  const isOverlay = shouldUseOverlay(content, location.pathname);

  return (
    <header
      className={`vilax-header${isOverlay ? ' vilax-header--overlay' : ' vilax-header--solid'}`}
      role="banner"
    >
      <div className="vilax-header__container">
        <div className="vilax-header__row">
          <div className="vilax-header__logo">
            <Logo />
          </div>
          <nav className="vilax-header__nav" aria-label="Main navigation">
            <Navigation pathname={location.pathname} />
          </nav>
          <div className="vilax-header__lang">
            <LanguageSelector />
          </div>
          {/*
           * SearchWidget renders two siblings as a Fragment:
           *   <div class="search-bar">...expanded form...</div>
           *   <div class="search-button"><button>🔍</button></div>
           * VLT's own CSS hides .search-bar off-screen and toggles `.active`
           * to slide it in — but that rule is tied to VLT's full DOM chain
           * which our markup doesn't reproduce. We implement the same
           * "absolute, off-screen until .active" pattern on our own
           * .vilax-header__search scope in vilax-layout.css.
           */}
          <div className="vilax-header__search">
            <SearchWidget />
          </div>
          {/*
           * Mobile navigation — OWNED (#1 shadow-paydown, DR-2026-203).
           * VilaxMobileNav (.vmn-*) is a bespoke hamburger + slide-in drawer
           * (nav + search + language selector), hidden >991px via
           * vilax-mobile.css. Replaces VLT's MobileNavigation +
           * MobileToolsFooter + the pastanaga-hamburger neutralization —
           * zero VLT-internal coupling.
           */}
          <div className="vilax-header__mobile-nav">
            <VilaxMobileNav pathname={location.pathname} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default VilaxHeader;
